/* Orbita: galáxia espiral em WebGL para fundo de login (Terachat, WebIP...).
 * ~1% das estrelas são "pacotes" na cor de destaque; redemoinho no cursor/dedo; ping opcional.
 * Componente autocontido, sem dependências além do React. Pausa com a aba oculta, respeita
 * prefers-reduced-motion, limita o DPR e baixa a resolução sozinho se não segurar ~40fps.
 *
 * Terachat (tela cheia):   <OrbitaBackground />
 * WebIP (dentro do painel): ver WEBIP_COLORS e o exemplo no fim do arquivo
 */
import { useEffect, useRef, useState, type CSSProperties } from 'react'

// ---------- infra ----------
type Frame<S> = (st: S, w: number, h: number, dpr: number, t: number, dt: number) => void

interface LoopOptions<S> {
  maxDpr?: number
  minDpr?: number
  setup?: (st: S, w: number, h: number, dpr: number) => void
  frame: Frame<S>
}

// RAF com: DPR limitado, pausa com aba oculta, quadro estático em reduced-motion
// e queda automática de resolução se a média ficar abaixo de ~40fps.
function runLoop<S>(canvas: HTMLCanvasElement, st: S, { maxDpr = 2, minDpr = 1, setup, frame }: LoopOptions<S>) {
  let dpr = Math.min(window.devicePixelRatio || 1, maxDpr)
  let w = 1, h = 1, raf = 0, last = 0, t = 0, alive = true
  let sum = 0, n = 0
  const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false

  const resize = () => {
    const r = canvas.getBoundingClientRect()
    w = Math.max(1, r.width); h = Math.max(1, r.height)
    canvas.width = Math.max(1, Math.round(w * dpr))
    canvas.height = Math.max(1, Math.round(h * dpr))
    setup?.(st, w, h, dpr)
    if (reduce) frame(st, w, h, dpr, 18, 0)
  }
  const tick = (now: number) => {
    if (!alive) return
    const dt = last ? Math.min(0.05, (now - last) / 1000) : 0.016
    last = now; t += dt
    frame(st, w, h, dpr, t, dt)
    sum += dt; n++
    if (n === 90) {
      const avg = sum / n; sum = 0; n = 0
      if (avg > 1 / 40 && dpr > minDpr) { dpr = Math.max(minDpr, dpr - 0.25); resize() }
    }
    raf = requestAnimationFrame(tick)
  }
  const start = () => { if (!raf && !reduce && alive) { last = 0; raf = requestAnimationFrame(tick) } }
  const stop = () => { cancelAnimationFrame(raf); raf = 0 }
  const onVis = () => (document.hidden ? stop() : start())
  const ro = new ResizeObserver(resize)
  ro.observe(canvas)
  document.addEventListener('visibilitychange', onVis)
  resize()
  start()
  return {
    destroy() { alive = false; stop(); ro.disconnect(); document.removeEventListener('visibilitychange', onVis) },
  }
}

interface Pointer {
  x: number; y: number; a: number   // posição suavizada (0..1, y para baixo) e presença
  rx: number; ry: number            // posição crua
  vx: number; vy: number            // velocidade em "telas menores por segundo" (y para baixo)
  step(dt: number): void; off(): void
}

// ponteiro relativo ao canvas; funciona com mouse e toque
function pointer(canvas: HTMLCanvasElement): Pointer {
  let tx = 0.5, ty = 0.5, ta = 0, lastT = 0, lastX = 0, lastY = 0, idle = 1
  const move = (e: PointerEvent) => {
    const r = canvas.getBoundingClientRect()
    const x = e.clientX - r.left, y = e.clientY - r.top
    tx = x / r.width; ty = y / r.height; ta = 1
    p.rx = tx; p.ry = ty
    const dts = (e.timeStamp - lastT) / 1000
    if (e.type === 'pointermove' && lastT && dts > 0.002 && dts < 0.2) {
      const m = Math.min(r.width, r.height) || 1
      const ivx = (x - lastX) / m / dts, ivy = (y - lastY) / m / dts
      p.vx = p.vx * 0.55 + ivx * 0.45; p.vy = p.vy * 0.55 + ivy * 0.45
      const sp = Math.hypot(p.vx, p.vy)
      if (sp > 6) { p.vx *= 6 / sp; p.vy *= 6 / sp }
    }
    lastT = e.timeStamp; lastX = x; lastY = y; idle = 0
  }
  const leave = () => { ta = 0 }
  const up = (e: PointerEvent) => { if (e.pointerType === 'touch') leave() }
  window.addEventListener('pointermove', move, { passive: true })
  window.addEventListener('pointerdown', move, { passive: true })
  window.addEventListener('pointerup', up, { passive: true })
  document.addEventListener('pointerleave', leave)
  const p: Pointer = {
    x: 0.5, y: 0.5, a: 0, rx: 0.5, ry: 0.5, vx: 0, vy: 0,
    step(dt) {
      const k = 1 - Math.exp(-dt * 4)
      p.x += (tx - p.x) * k; p.y += (ty - p.y) * k; p.a += (ta - p.a) * (1 - Math.exp(-dt * 2))
      idle += dt
      if (idle > 0.06) { const d = Math.exp(-dt * 14); p.vx *= d; p.vy *= d } // parou de mexer: zera rápido
    },
    off() {
      window.removeEventListener('pointermove', move)
      window.removeEventListener('pointerdown', move)
      window.removeEventListener('pointerup', up)
      document.removeEventListener('pointerleave', leave)
    },
  }
  return p
}

// ---------- tipos públicos ----------
export interface OrbitaColors {
  bg: string      // fundo do container/shader
  glow: string    // névoa em volta da galáxia
  core: string    // estrelas do núcleo
  mid: string     // estrelas do meio do disco
  rim: string     // estrelas da borda (puxe para o tom do fundo)
  accent: string  // "pacotes": ~1% das estrelas, piscando
}

export interface OrbitaOptions {
  colors?: Partial<OrbitaColors>
  /** centro da galáxia em fração do container, [x da esquerda, y do topo]. Sem valor: responsivo */
  center?: [number, number]
  /** escala da galáxia. Sem valor: responsivo (1.25 retrato, 1.75 paisagem) */
  zoom?: number
  /** número de estrelas. Sem valor: 14k (<640px) ou 32k */
  count?: number
  /** anel de "ping" saindo de alguns pacotes, de vez em quando */
  ping?: boolean
}

export const TERACHAT_COLORS: OrbitaColors = {
  bg: '#02172C', glow: '#1A3361', core: '#FFEDD1', mid: '#8CBFFF', rim: '#29529E', accent: '#54E52E',
}

const hex3 = (h: string): [number, number, number] => {
  const n = parseInt(h.replace('#', ''), 16)
  return [((n >> 16) & 255) / 255, ((n >> 8) & 255) / 255, (n & 255) / 255]
}

// ---------- vento ----------
// Grade pequena em espaço de tela. O cursor sopra na direção em que se move; o vento
// perde força rápido (arrasto) e o deslocamento volta devagar (retorno), como areia
// assentando. Unidades: fração do lado menor do canvas. Linha 0 = base (y para cima).
const WIND = {
  cells: 48,      // células no lado menor
  sigma: 0.075,   // raio do sopro
  gain: 0.45,     // fração da velocidade do cursor que vira vento
  spread: 0.18,   // empurrão para os lados do rastro, proporcional à velocidade
  drag: 3.2,      // 1/s: quão rápido o vento morre
  ret: 1,       // 1/s: quão rápido a areia volta (0.5 -> ~2s para assentar)
  diffuse: 4,     // 1/s: quanto o deslocamento se espalha para as vizinhas
  max: 0.16,      // deslocamento máximo
}

class WindField {
  cols = 1; rows = 1
  d = new Float32Array(2); v = new Float32Array(2); tmp = new Float32Array(2)
  bytes = new Uint8Array(4)
  active = false
  resize(w: number, h: number) {
    const m = Math.min(w, h)
    this.cols = Math.max(4, Math.round((WIND.cells * w) / m)); this.rows = Math.max(4, Math.round((WIND.cells * h) / m))
    const n = this.cols * this.rows
    this.d = new Float32Array(n * 2); this.v = new Float32Array(n * 2); this.tmp = new Float32Array(n * 2)
    this.bytes = new Uint8Array(n * 4).fill(128)
    for (let i = 3; i < this.bytes.length; i += 4) { this.bytes[i - 1] = 0; this.bytes[i] = 255 }
    this.active = true
  }
  /** cx, cy em 0..1 (y para cima); vx, vy em lados-menores/s (y para cima). Retorna se algo mudou. */
  step(dt: number, w: number, h: number, cx: number, cy: number, vx: number, vy: number): boolean {
    const { cols, rows, d, v, tmp } = this
    const speed = Math.hypot(vx, vy)
    if (!this.active && speed < 0.02) return false
    const m = Math.min(w, h), sx = w / m, sy = h / m
    const s2 = 2 * WIND.sigma * WIND.sigma, pull = Math.min(1, dt * 10)
    const kDrag = Math.exp(-dt * WIND.drag), kRet = Math.exp(-dt * WIND.ret)
    let peak = 0
    for (let j = 0, i = 0; j < rows; j++) {
      const dy = ((j + 0.5) / rows - cy) * sy
      for (let c = 0; c < cols; c++, i += 2) {
        if (speed >= 0.02) {
          const dx = ((c + 0.5) / cols - cx) * sx
          const r2 = dx * dx + dy * dy
          if (r2 < s2 * 4.6) { // até ~3 sigma
            const fall = Math.exp(-r2 / s2), r = Math.sqrt(r2) + 1e-4
            const tx = vx * WIND.gain + (dx / r) * speed * WIND.spread
            const ty = vy * WIND.gain + (dy / r) * speed * WIND.spread
            v[i] += (tx - v[i]) * fall * pull; v[i + 1] += (ty - v[i + 1]) * fall * pull
          }
        }
        v[i] *= kDrag; v[i + 1] *= kDrag
        let px = (d[i] + v[i] * dt) * kRet, py = (d[i + 1] + v[i + 1] * dt) * kRet
        const l = Math.hypot(px, py)
        if (l > WIND.max) { px *= WIND.max / l; py *= WIND.max / l }
        d[i] = px; d[i + 1] = py
        if (l > peak) peak = l
      }
    }
    // difusão: deslocamento escorre para as vizinhas (o "monte" se espalha)
    const kd = (1 - Math.exp(-dt * WIND.diffuse)) * 0.5
    tmp.set(d)
    for (let j = 0, i = 0; j < rows; j++) {
      for (let c = 0; c < cols; c++, i += 2) {
        const l = c > 0 ? i - 2 : i, r = c < cols - 1 ? i + 2 : i
        const u = j < rows - 1 ? i + cols * 2 : i, b = j > 0 ? i - cols * 2 : i
        d[i] += ((tmp[l] + tmp[r] + tmp[u] + tmp[b]) * 0.25 - tmp[i]) * kd
        d[i + 1] += ((tmp[l + 1] + tmp[r + 1] + tmp[u + 1] + tmp[b + 1]) * 0.25 - tmp[i + 1]) * kd
      }
    }
    const wasActive = this.active
    this.active = peak > 2e-4 || speed >= 0.02
    if (!this.active) { d.fill(0); v.fill(0) }
    if (!this.active && !wasActive) return false
    const bytes = this.bytes, inv = 127 / WIND.max
    for (let i = 0, o = 0; i < d.length; i += 2, o += 4) {
      bytes[o] = 128 + Math.round(d[i] * inv); bytes[o + 1] = 128 + Math.round(d[i + 1] * inv)
      bytes[o + 2] = Math.min(255, Math.round((Math.hypot(d[i], d[i + 1]) / WIND.max) * 255))
    }
    return true
  }
}

// ---------- shaders ----------
const GLSL_HASH = 'float h(vec2 p){p=fract(p*vec2(123.34,456.21));p+=dot(p,p+45.32);return fract(p.x*p.y);}'

const VS = `precision highp float;
attribute vec4 a; uniform vec2 R; uniform float T,D,S,Z,P,MX; uniform vec3 M; uniform vec2 C;
uniform vec3 cCore,cMid,cRim,cAcc; uniform sampler2D F;
varying vec4 vC; varying vec3 vP;
void main(){
  float r=a.x, s=a.w;
  float th=a.y + T*0.06/(pow(r,1.1)+0.25);            // rotação diferencial
  float y=a.z*(0.02+0.07*exp(-r*5.));
  vec3 p=vec3(cos(th)*r, y, sin(th)*r);
  float tilt=1.12+(M.y-.5)*.12*M.z, yaw=T*.015+(M.x-.5)*.15*M.z;
  p.xz=mat2(cos(yaw),-sin(yaw),sin(yaw),cos(yaw))*p.xz;
  p.yz=mat2(cos(tilt),-sin(tilt),sin(tilt),cos(tilt))*p.yz;
  float f=1.9/(p.z+2.4);
  float asp=R.x/R.y;
  gl_Position=vec4(p.x*f*Z/asp + C.x, p.y*f*Z + C.y, 0.,1.);
  // vento: cada grão reage com força e ângulo próprios, e tremula enquanto está fora do lugar
  vec4 fw=__WIND_SAMPLE__;
  vec2 wd=(fw.xy*255.-128.)/127.*MX; float wm=fw.z;
  float wg=.45+1.1*fract(s*91.7), wa=(fract(s*53.3)-.5)*1.2;
  vec2 dd=mat2(cos(wa),sin(wa),-sin(wa),cos(wa))*wd*wg;
  dd+=vec2(sin(T*2.3+s*311.),cos(T*1.9+s*173.))*length(wd)*.12;
  gl_Position.xy+=dd*min(R.x,R.y)/(R*.5);
  bool msg=s>0.991;
  float tw=0.6+0.4*sin(T*(1.5+s*4.)+s*60.);
  float base=(msg?4.2:1.0+s*1.5)*f*max(D,1.6)*(msg?tw:1.)*(1.+wm*.5); // piso p/ monitores DPR 1
  // ping: só os ~0.3% mais "fortes", cada um ~4% do tempo
  float ph=fract(T*.05+s*37.); float ring=(P>.5 && s>.997 && ph<.04)?ph/.04:0.;
  float sz=base+ring*46.*D;
  gl_PointSize=min(sz,S);
  vP=vec3(msg?1.:0., ring, base/max(sz,1.));
  vec3 c=mix(cCore,cMid,smoothstep(.0,.35,r)); c=mix(c,cRim,smoothstep(.35,1.1,r));
  float al=(0.55+0.45*s)*smoothstep(1.3,.6,r)*(0.35+0.65*smoothstep(0.,.25,r));
  if(msg){c=cAcc; al=0.95*tw;}
  c=mix(c,vec3(1.),wm*.3);
  vC=vec4(c,min(1.,al*(r<.18?.55:1.)*(1.+wm*1.3)));
}`

const FS = `precision mediump float; varying vec4 vC; varying vec3 vP;
void main(){
  vec2 q=gl_PointCoord-.5; float d=length(q)*2.; if(d>1.)discard;
  if(vP.y>0.){                                           // sprite crescido: núcleo + anel
    float core=smoothstep(vP.z,vP.z*.3,d);
    float ring=smoothstep(.07,0.,abs(d-.92))*(1.-vP.y)*.55;
    gl_FragColor=vec4(vC.rgb, max(core*vC.a,ring)); return;
  }
  float e=d*d; gl_FragColor=vec4(vC.rgb, vC.a*(1.-e)*(1.-e));
}`

const BG = `precision mediump float; uniform vec2 R; uniform float T,Z; uniform vec2 C; uniform vec3 cBg,cGlow,cCore; ${GLSL_HASH}
void main(){
  vec2 uv=gl_FragCoord.xy/R; float asp=R.x/R.y;
  vec2 c=C*.5+.5; vec2 d=(uv-c)*vec2(asp,1.);
  float g=exp(-dot(d,d)*14./(Z*Z));
  vec3 col=cBg*(1.-length(uv-.5)*.55);
  col+=cGlow*g*.55 + cCore*pow(g,6.)*.22;
  col+=(h(gl_FragCoord.xy+fract(T)*37.)-.5)*.018;          // grão contra banding
  gl_FragColor=vec4(col,1.);
}`

// ---------- efeito ----------
export function mountOrbita(canvas: HTMLCanvasElement, opts: OrbitaOptions = {}): () => void {
  const gl = canvas.getContext('webgl', { antialias: false, alpha: false, powerPreference: 'low-power' })
  if (!gl) return () => { } // sem WebGL: fica o fundo do container
  const col = { ...TERACHAT_COLORS, ...opts.colors }
  const ptr = pointer(canvas)

  const sh = (type: number, src: string) => {
    const o = gl.createShader(type)!
    gl.shaderSource(o, src); gl.compileShader(o)
    if (!gl.getShaderParameter(o, gl.COMPILE_STATUS)) throw new Error(gl.getShaderInfoLog(o) ?? 'shader')
    return o
  }
  const prog = (v: string, f: string) => {
    const p = gl.createProgram()!
    gl.attachShader(p, sh(gl.VERTEX_SHADER, v)); gl.attachShader(p, sh(gl.FRAGMENT_SHADER, f)); gl.linkProgram(p)
    return p
  }
  // vento precisa de textura no vertex shader (quase todo aparelho tem; sem ela, fica sem interação)
  const windOn = (gl.getParameter(gl.MAX_VERTEX_TEXTURE_IMAGE_UNITS) as number) > 0
  const bgP = prog('attribute vec2 p;void main(){gl_Position=vec4(p,0.,1.);}', BG)
  const ptP = prog(VS.replace('__WIND_SAMPLE__', windOn ? 'texture2D(F,gl_Position.xy*.5+.5)' : 'vec4(128./255.,128./255.,0.,1.)'), FS)

  const wind = new WindField()
  const windTex = gl.createTexture()
  let texCols = 0, texRows = 0
  gl.bindTexture(gl.TEXTURE_2D, windTex)
  for (const [k, v] of [[gl.TEXTURE_MIN_FILTER, gl.LINEAR], [gl.TEXTURE_MAG_FILTER, gl.LINEAR], [gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE], [gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE]])
    gl.texParameteri(gl.TEXTURE_2D, k, v)

  const quad = gl.createBuffer(); gl.bindBuffer(gl.ARRAY_BUFFER, quad)
  gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW)

  // estrelas: [raio, ângulo inicial, altura, semente]
  const rnd = Math.random
  const g3 = () => rnd() + rnd() + rnd() - 1.5 // gaussiana barata
  const n = opts.count ?? (canvas.getBoundingClientRect().width < 640 ? 14000 : 32000)
  const data = new Float32Array(n * 4)
  for (let i = 0, o = 0; i < n; i++, o += 4) {
    const s = rnd()
    if (rnd() < 0.18) { // bojo esférico no centro
      data[o] = Math.abs(g3()) * 0.22 + 0.01; data[o + 1] = rnd() * Math.PI * 2; data[o + 2] = g3() * 2.2; data[o + 3] = s
      continue
    }
    const r = Math.pow(rnd(), 1.3) * 1.15 + 0.08
    const arm = Math.floor(rnd() * 2)
    data[o] = r
    data[o + 1] = arm * Math.PI + Math.log(r) * 1.15 + g3() * (0.55 + r * 0.35) * (rnd() < 0.3 ? 2.6 : 1)
    data[o + 2] = g3() * 0.7
    data[o + 3] = s
  }
  const pts = gl.createBuffer(); gl.bindBuffer(gl.ARRAY_BUFFER, pts)
  gl.bufferData(gl.ARRAY_BUFFER, data, gl.STATIC_DRAW)

  const maxPt = (gl.getParameter(gl.ALIASED_POINT_SIZE_RANGE) as Float32Array)[1]
  const U = (p: WebGLProgram, k: string) => gl.getUniformLocation(p, k)
  const aBg = gl.getAttribLocation(bgP, 'p'), aPt = gl.getAttribLocation(ptP, 'a')

  // cores são fixas: envia uma vez por programa
  gl.useProgram(bgP)
  gl.uniform3fv(U(bgP, 'cBg'), hex3(col.bg)); gl.uniform3fv(U(bgP, 'cGlow'), hex3(col.glow)); gl.uniform3fv(U(bgP, 'cCore'), hex3(col.core))
  gl.useProgram(ptP)
  gl.uniform3fv(U(ptP, 'cCore'), hex3(col.core)); gl.uniform3fv(U(ptP, 'cMid'), hex3(col.mid))
  gl.uniform3fv(U(ptP, 'cRim'), hex3(col.rim)); gl.uniform3fv(U(ptP, 'cAcc'), hex3(col.accent))
  gl.uniform1f(U(ptP, 'P'), opts.ping ? 1 : 0)
  gl.uniform1f(U(ptP, 'MX'), WIND.max)
  gl.uniform1i(U(ptP, 'F'), 0)

  const bgU = { R: U(bgP, 'R'), T: U(bgP, 'T'), Z: U(bgP, 'Z'), C: U(bgP, 'C') }
  const ptU = { R: U(ptP, 'R'), T: U(ptP, 'T'), M: U(ptP, 'M'), D: U(ptP, 'D'), S: U(ptP, 'S'), Z: U(ptP, 'Z'), C: U(ptP, 'C') }

  const loop = runLoop<null>(canvas, null, {
    maxDpr: 2, minDpr: 1,
    frame(_st, w, h, dpr, t, dt) {
      ptr.step(dt || 0.016)
      const T = t + 30
      const portrait = w < h
      const zoom = opts.zoom ?? (portrait ? 1.25 : 1.75)
      const [cx, cy] = opts.center ?? (portrait ? [0.5, 0.425] : [0.71, 0.395])
      const Cx = cx * 2 - 1, Cy = 1 - cy * 2 // fração do container -> clip space
      gl.viewport(0, 0, canvas.width, canvas.height)

      // simula o vento e sobe a textura só quando algo mudou
      if (windOn) {
        if (wind.cols * wind.rows <= 1 || Math.abs(wind.cols / wind.rows - w / h) > 0.05) wind.resize(w, h)
        const changed = wind.step(Math.min(dt || 0, 0.05), w, h, ptr.rx, 1 - ptr.ry, ptr.vx, -ptr.vy)
        gl.activeTexture(gl.TEXTURE0); gl.bindTexture(gl.TEXTURE_2D, windTex)
        if (texCols !== wind.cols || texRows !== wind.rows) {
          texCols = wind.cols; texRows = wind.rows
          gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, texCols, texRows, 0, gl.RGBA, gl.UNSIGNED_BYTE, wind.bytes)
        } else if (changed) {
          gl.texSubImage2D(gl.TEXTURE_2D, 0, 0, 0, texCols, texRows, gl.RGBA, gl.UNSIGNED_BYTE, wind.bytes)
        }
      }

      gl.disable(gl.BLEND)
      gl.useProgram(bgP)
      gl.bindBuffer(gl.ARRAY_BUFFER, quad)
      gl.enableVertexAttribArray(aBg); gl.vertexAttribPointer(aBg, 2, gl.FLOAT, false, 0, 0)
      gl.uniform2f(bgU.R, canvas.width, canvas.height); gl.uniform1f(bgU.T, T); gl.uniform1f(bgU.Z, zoom); gl.uniform2f(bgU.C, Cx, Cy)
      gl.drawArrays(gl.TRIANGLES, 0, 3)
      gl.disableVertexAttribArray(aBg)

      gl.enable(gl.BLEND); gl.blendFunc(gl.SRC_ALPHA, gl.ONE) // aditivo
      gl.useProgram(ptP)
      gl.bindBuffer(gl.ARRAY_BUFFER, pts)
      gl.enableVertexAttribArray(aPt); gl.vertexAttribPointer(aPt, 4, gl.FLOAT, false, 0, 0)
      gl.uniform2f(ptU.R, canvas.width, canvas.height); gl.uniform1f(ptU.T, T)
      gl.uniform3f(ptU.M, ptr.x, 1 - ptr.y, ptr.a); gl.uniform1f(ptU.D, dpr); gl.uniform1f(ptU.S, maxPt)
      gl.uniform1f(ptU.Z, zoom); gl.uniform2f(ptU.C, Cx, Cy)
      gl.drawArrays(gl.POINTS, 0, n)
      gl.disableVertexAttribArray(aPt)
    },
  })
  return () => { loop.destroy(); ptr.off(); gl.getExtension('WEBGL_lose_context')?.loseContext() }
}

// ---------- componente ----------
type Props = OrbitaOptions & { className?: string; style?: CSSProperties }

// Cada montagem cria o próprio <canvas>: assim o double-mount do StrictMode
// não reaproveita um contexto WebGL já destruído.
export default function OrbitaBackground({ className, style, colors, center, zoom, count, ping }: Props) {
  const host = useRef<HTMLDivElement>(null)
  // remonta só quando o valor muda de fato (arrays/objetos novos a cada render não contam)
  const key = JSON.stringify([colors, center, zoom, count, ping])

  useEffect(() => {
    const el = host.current
    if (!el) return
    const canvas = document.createElement('canvas')
    canvas.style.cssText = 'position:absolute;inset:0;width:100%;height:100%;display:block'
    el.appendChild(canvas)
    const [c, ce, z, n, p] = JSON.parse(key) as [OrbitaOptions['colors'], OrbitaOptions['center'], number | null, number | null, boolean | null]
    const destroy = mountOrbita(canvas, { colors: c, center: ce, zoom: z ?? undefined, count: n ?? undefined, ping: p ?? undefined })
    return () => { destroy(); canvas.remove() }
  }, [key])

  return (
    <div
      ref={host}
      aria-hidden="true"
      className={className}
      style={{ position: 'fixed', inset: 0, zIndex: 0, overflow: 'hidden', background: colors?.bg ?? TERACHAT_COLORS.bg, ...style }}
    />
  )
}

/** true enquanto a media query casar. Use para só montar o fundo quando o painel estiver visível. */
export function useMediaQuery(query: string): boolean {
  const [match, setMatch] = useState(() => typeof window !== 'undefined' && window.matchMedia(query).matches)
  useEffect(() => {
    const mq = window.matchMedia(query)
    const on = () => setMatch(mq.matches)
    on(); mq.addEventListener('change', on)
    return () => mq.removeEventListener('change', on)
  }, [query])
  return match
}

// ---------- preset WebIP ----------
export const WEBIP_COLORS: OrbitaColors = {
  bg: '#0C2E5E', glow: '#123264', core: '#FFF4DE', mid: '#A8CBF5', rim: '#3D6AA8', accent: '#7CD022',
}

/* Exemplo no login da WebIP (painel some abaixo de 1024px, então nem monta no mobile):
 *
 * function PainelMarca() {
 *   const desktop = useMediaQuery('(min-width: 1024px)')
 *   return (
 *     <aside className="painel-marca" style={{ position: 'relative', overflow: 'hidden' }}>
 *       {desktop && (
 *         <OrbitaBackground
 *           colors={WEBIP_COLORS}
 *           center={[0.6, 0.3]}
 *           zoom={1.45}
 *           count={20000}
 *           ping
 *           style={{ position: 'absolute' }}
 *         />
 *       )}
 *       <div className="veu" />       // gradiente navy atrás do título
 *       <h1>A sua rede, em um único <em>painel.</em></h1>
 *     </aside>
 *   )
 * }
 */